import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { format, parseISO } from 'date-fns'

const empty = { club_name: '', contact_name: '', contact_role: '', country: '', date_contacted: '', method: '', status: 'pending', response: '', notes: '', follow_up_date: '' }
const statuses = ['pending', 'no response', 'positive', 'negative', 'trial', 'signed']
const methods = ['Email', 'WhatsApp', 'Instagram', 'LinkedIn', 'Phone', 'In Person', 'Agent', 'Other']

export default function Networking({ session }) {
  const uid = session.user.id
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState(empty)
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')

  useEffect(() => { load() }, [])

  const load = async () => {
    const { data } = await supabase.from('networking').select('*').eq('user_id', uid).order('date_contacted', { ascending: false })
    setItems(data || [])
    setLoading(false)
  }

  const save = async () => {
    setSaving(true)
    const payload = { ...form, user_id: uid }
    if (editing) {
      await supabase.from('networking').update(payload).eq('id', editing)
    } else {
      await supabase.from('networking').insert(payload)
    }
    await load()
    setSaving(false)
    setSaved(true)
    setTimeout(() => {
      setShowModal(false)
      setEditing(null)
      setForm(empty)
      setSaved(false)
    }, 700)
  }

  const del = async (id) => {
    if (!window.confirm('Delete this entry?')) return
    await supabase.from('networking').delete().eq('id', id)
    setItems(prev => prev.filter(i => i.id !== id))
  }

  const openEdit = (item) => {
    setForm(item)
    setEditing(item.id)
    setShowModal(true)
  }

  const filtered = items
    .filter(i => filter === 'all' ? true : i.status === filter)
    .filter(i => !search || i.club_name?.toLowerCase().includes(search.toLowerCase()) || i.country?.toLowerCase().includes(search.toLowerCase()))

  const counts = statuses.reduce((acc, s) => ({ ...acc, [s]: items.filter(i => i.status === s).length }), {})

  const statusColor = (s) => {
    if (s === 'positive' || s === 'signed') return 'badge-green'
    if (s === 'negative' || s === 'no response') return 'badge-red'
    if (s === 'trial') return 'badge-gold'
    return 'badge-grey'
  }

  if (loading) return <div style={{ color: 'var(--muted)' }}>Loading...</div>

  return (
    <div>
      <div className="flex-between mb-32">
        <div>
          <div className="page-title">Networking</div>
          <div className="page-sub">Track your own club outreach</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setForm(empty); setEditing(null); setShowModal(true) }}>+ Log Outreach</button>
      </div>

      {/* Stats */}
      <div className="grid-4 mb-32">
        {[
          { value: items.length, label: 'Total Contacts' },
          { value: counts['positive'] || 0, label: 'Positive' },
          { value: counts['trial'] || 0, label: 'Trials' },
          { value: counts['signed'] || 0, label: 'Signed' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-value">{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters + Search */}
      <div className="flex-between mb-16" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div className="flex gap-8" style={{ flexWrap: 'wrap' }}>
          <button onClick={() => setFilter('all')} className="btn btn-ghost" style={{ fontSize: 12, background: filter === 'all' ? 'var(--gold-dim)' : 'transparent', borderColor: filter === 'all' ? 'var(--gold-border)' : 'var(--border)', color: filter === 'all' ? 'var(--gold)' : 'var(--muted)' }}>All ({items.length})</button>
          {['pending', 'positive', 'trial', 'signed', 'negative'].map(s => (
            <button key={s} onClick={() => setFilter(s)} className="btn btn-ghost" style={{ fontSize: 12, background: filter === s ? 'var(--gold-dim)' : 'transparent', borderColor: filter === s ? 'var(--gold-border)' : 'var(--border)', color: filter === s ? 'var(--gold)' : 'var(--muted)' }}>
              {s.charAt(0).toUpperCase() + s.slice(1)} ({counts[s] || 0})
            </button>
          ))}
        </div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search clubs..." style={{ width: 200 }} />
      </div>

      {filtered.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">⟳</div>
            <div className="empty-state-title">No outreach logged</div>
            <div className="empty-state-body">Track every club you contact — stay organised, follow up on time, and measure your progress.</div>
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>Log First Outreach</button>
          </div>
        </div>
      ) : (
        <div className="card">
          {filtered.map(item => (
            <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', flexWrap: 'wrap', gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: 15 }}>{item.club_name}</span>
                  <span className={`badge ${statusColor(item.status)}`}>{item.status}</span>
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 13, color: 'var(--muted)' }}>
                  {item.country && <span>{item.country}</span>}
                  {item.contact_name && <span>{item.contact_name}</span>}
                  {item.method && <span>{item.method}</span>}
                  {item.date_contacted && <span>{format(parseISO(item.date_contacted), 'dd MMM yy')}</span>}
                  {item.follow_up_date && <span style={{ color: new Date(item.follow_up_date) < new Date() ? '#E53935' : 'var(--muted)' }}>Follow up: {format(parseISO(item.follow_up_date), 'dd MMM')}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button onClick={() => openEdit(item)} className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }}>Edit</button>
                <button onClick={() => del(item.id)} className="btn btn-danger" style={{ padding: '4px 10px', fontSize: 12 }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-title">{editing ? 'Edit Outreach' : 'Log Outreach'}</div>
            <div className="grid-2 mb-16">
              <div><label>Club Name</label><input value={form.club_name || ''} onChange={e => setForm(p => ({...p, club_name: e.target.value}))} placeholder="FC Example" /></div>
              <div><label>Country</label><input value={form.country || ''} onChange={e => setForm(p => ({...p, country: e.target.value}))} placeholder="Poland" /></div>
            </div>
            <div className="grid-2 mb-16">
              <div><label>Contact Name</label><input value={form.contact_name || ''} onChange={e => setForm(p => ({...p, contact_name: e.target.value}))} placeholder="Coach / Director" /></div>
              <div><label>Contact Role</label><input value={form.contact_role || ''} onChange={e => setForm(p => ({...p, contact_role: e.target.value}))} placeholder="Head Coach" /></div>
            </div>
            <div className="grid-2 mb-16">
              <div><label>Date Contacted</label><input type="date" value={form.date_contacted || ''} onChange={e => setForm(p => ({...p, date_contacted: e.target.value}))} /></div>
              <div>
                <label>Method</label>
                <select value={form.method || ''} onChange={e => setForm(p => ({...p, method: e.target.value}))}>
                  <option value="">Select method</option>
                  {methods.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div className="grid-2 mb-16">
              <div>
                <label>Status</label>
                <select value={form.status || 'pending'} onChange={e => setForm(p => ({...p, status: e.target.value}))}>
                  {statuses.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              </div>
              <div><label>Follow Up Date</label><input type="date" value={form.follow_up_date || ''} onChange={e => setForm(p => ({...p, follow_up_date: e.target.value}))} /></div>
            </div>
            <div className="mb-16"><label>Response</label><input value={form.response || ''} onChange={e => setForm(p => ({...p, response: e.target.value}))} placeholder="What did they say?" /></div>
            <div className="mb-24"><label>Notes</label><textarea value={form.notes || ''} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={2} placeholder="Any additional notes..." /></div>
            <div className="flex gap-8">
              <button className="btn btn-primary" onClick={save} disabled={saving || saved}>{saving ? <><span className="btn-spinner" /> Saving...</> : saved ? '✓ Saved' : 'Save'}</button>
              <button className="btn btn-ghost" onClick={() => { setShowModal(false); setEditing(null); setForm(empty) }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
